import { useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { common, controls } from '@ocean/ui'
import { DateUtils } from '@ocean/utils'
import { EnvironmentUtils, UserUtils } from 'utils'
import { AccountTypes } from 'modules/Auth/types'
import { WartsilaButtonWrapper } from 'modules/simulatorTrainings/common/components'
import { HintVariant } from 'modules/simulatorTrainings/common/components/common/Hint'
import {
  SessionListItem,
  SessionListItemHeader,
  SessionListItemActions,
  SessionListItemInfoWrapper,
  SessionListItemInfo,
  SessionListItemInfoLabel,
  SessionListItemSidebar,
  PleaseWait,
  Hint,
} from 'modules/simulatorTrainings/common/components/common/ScheduleSession.styled'
import { EnrolledPersonContext } from 'modules/simulatorTrainings/common/contexts'
import {
  CLASSROOM_ENDED_SESSION_STATES,
  CLASSROOM_START_ACTIVE_STATES,
  CLASSROOM_STOP_ACTIVE_STATES,
} from 'modules/simulatorTrainings/common/hooks/useScheduleSessionState'
import { namespace } from 'modules/simulatorTrainings/common/localization'
import {
  SimulatorTrainingScheduleSession,
  SimulatorTrainingSessionType,
  WartsilaClassroomSessionState,
} from 'modules/simulatorTrainings/common/types'

const { Typography } = common
const { Button, ButtonVariant } = controls

const I18_PATH = `${namespace}:simulatorTraining.scheduleSimulatorSession`

type ScheduleSimulatorSessionProps = {
  session: SimulatorTrainingScheduleSession
  sessionTypes: Record<SimulatorTrainingSessionType, string>
  sessionState?: WartsilaClassroomSessionState
  needHideActions?: boolean
  stopIsLoading?: boolean
  sessionStateRequestCountdown?: number
  isCurrentMaintenance: boolean
  isFailed?: boolean
  onStartSimulation: (sessionId: Guid) => void
  onStopSimulation: (sessionId: Guid) => void
}

const ScheduleSimulatorSession: React.FC<ScheduleSimulatorSessionProps> = props => {
  const { t } = useTranslation()
  const {
    session,
    onStartSimulation,
    sessionTypes,
    onStopSimulation,
    sessionState,
    needHideActions,
    stopIsLoading,
    sessionStateRequestCountdown,
    isCurrentMaintenance,
    isFailed,
  } = props
  const isUserEnrolledPerson = useContext(EnrolledPersonContext)
  const { sessionId, sessionName, type, startDate, endDate } = session
  const localStartDate = DateUtils.convertUTCDateToLocalDate(startDate)
  const isStartDisabled =
    isCurrentMaintenance ||
    sessionState === undefined ||
    !CLASSROOM_START_ACTIVE_STATES.includes(sessionState)
  const { type: userType } = UserUtils.getUserAccountInfo()
  const isSpecialUser = userType === AccountTypes.SpecialUser
  const { instructorLedStartSessionAvailable } =
    EnvironmentUtils.getPermissions('simulatorTraining')

  // TODO: Probably need refactoring to one switch-function (hook)
  // for preventing unnecessary calculation of constants
  const isStopHidden =
    !isSpecialUser ||
    sessionState === undefined ||
    !CLASSROOM_STOP_ACTIVE_STATES.includes(sessionState)
  const isSessionStarting = sessionState === WartsilaClassroomSessionState.Starting
  const isNotEndedSession =
    DateUtils.convertUTCDateToLocalDate(endDate).getTime() > new Date().getTime()
  const showSimulationActions =
    sessionState === undefined || !CLASSROOM_ENDED_SESSION_STATES.includes(sessionState)
  const showActions =
    isUserEnrolledPerson &&
    !needHideActions &&
    (!isSpecialUser || instructorLedStartSessionAvailable) &&
    isNotEndedSession &&
    showSimulationActions
  const hintMessage = getHintMessage()
  const hintVariant = getHintVariant()

  function getHintMessage() {
    if (isCurrentMaintenance) return
    switch (true) {
      case isFailed:
        return t(`${I18_PATH}.sessionFailedMessage`)
      case isSessionStarting:
        return t(`${I18_PATH}.sessionStartingMessage`, { count: sessionStateRequestCountdown })
      default:
        return null
    }
  }

  function getHintVariant() {
    switch (true) {
      case isFailed:
        return HintVariant.Error
      default:
        return HintVariant.Warning
    }
  }

  function handleStartClick() {
    onStartSimulation(sessionId)
  }

  function handleStopSimulation() {
    onStopSimulation(sessionId)
  }

  function renderJoin() {
    if (isSessionStarting) return <PleaseWait>{t(`${I18_PATH}.pleaseWait`)}</PleaseWait>
    return (
      <Button
        title={t(`${I18_PATH}.join`)}
        disabled={isStartDisabled}
        onClick={handleStartClick}
        dataTest='ScheduleSimulatorSessionSimulatorStart'
      />
    )
  }

  function renderActions() {
    const isInactiveSession = !isSessionStarting && isStartDisabled
    return (
      <WartsilaButtonWrapper
        inactive={isCurrentMaintenance || isInactiveSession}
        animated={isSessionStarting}
      >
        {!isStopHidden && (
          <Button
            title={t(`${I18_PATH}.stopSimulator`)}
            onClick={handleStopSimulation}
            variant={ButtonVariant.Danger}
            disabled={stopIsLoading || isInactiveSession}
            dataTest='ScheduleSimulatorSessionSimulatorStop'
          />
        )}
        {renderJoin()}
      </WartsilaButtonWrapper>
    )
  }

  return (
    <SessionListItem key={sessionId}>
      <SessionListItemHeader>
        <Typography variant='small' data-test='ScheduleSimulatorSessionType'>
          {sessionTypes[type]}
        </Typography>
        <Typography variant='h3' data-test='ScheduleSimulatorSessionName'>
          {sessionName}
        </Typography>
      </SessionListItemHeader>
      {hintMessage && <Hint variant={hintVariant}>{hintMessage}</Hint>}
      <SessionListItemActions>
        <SessionListItemInfoWrapper>
          <SessionListItemInfo>
            <SessionListItemInfoLabel>
              <Typography
                variant='small'
                uppercase
                data-test='ScheduleSimulatorSessionStartTimeLabel'
              >
                {t(`${I18_PATH}.startTime`)}
              </Typography>
            </SessionListItemInfoLabel>
            <Typography variant='small' data-test='ScheduleSimulatorSessionStartTime'>
              {DateUtils.formatShortTime(localStartDate)}
            </Typography>
          </SessionListItemInfo>
          <SessionListItemInfo>
            <SessionListItemInfoLabel>
              <Typography variant='small' uppercase data-test='ScheduleSimulatorSessionType'>
                {t(`${I18_PATH}.duration`)}
              </Typography>
            </SessionListItemInfoLabel>
            <Typography variant='small' data-test='ScheduleSimulatorSessionDuration'>
              {DateUtils.getDurationBetweenDates(startDate, endDate)}
            </Typography>
          </SessionListItemInfo>
        </SessionListItemInfoWrapper>
        {showActions && <SessionListItemSidebar>{renderActions()}</SessionListItemSidebar>}
      </SessionListItemActions>
    </SessionListItem>
  )
}

export default ScheduleSimulatorSession
